import { useMemo } from "react";
import { useListSettingsStore } from "@/lib/store/listSettingsStore";
import type { ListSettingsFieldConfig } from "./listSettingsTypes";

type UseListSettingsParams = {
  key: string; // e.g. "inventory.items"
  availableFields: ListSettingsFieldConfig[];
  defaultVisibleOrder: string[]; // e.g. ITEM_LIST_VISIBLE_DEFAULT_FIELDS or subset
};

export function useListSettings(params: UseListSettingsParams) {
  const { key, availableFields, defaultVisibleOrder } = params;
  
  const storedSettings = useListSettingsStore((state) => state.settingsByKey[key]);
  const updateSettings = useListSettingsStore((state) => state.updateSettings);
  const resetSettings = useListSettingsStore((state) => state.resetSettings);

  // Compute visible field order (stored or default)
  // CRITICAL: Sanitize persisted order against availableFields to prevent stale fieldnames
  // that could cause backend "Unknown column" errors
  const visibleFieldOrder = useMemo(() => {
    const availableFieldnames = new Set(availableFields.map(f => f.fieldname));
    const requiredFieldnames = new Set(
      availableFields.filter(f => f.required).map(f => f.fieldname)
    );
    
    if (storedSettings?.visibleFieldOrder) {
      // Sanitize: filter to only available fields, preserve order, dedupe
      const sanitized = storedSettings.visibleFieldOrder
        .filter(fieldname => availableFieldnames.has(fieldname))
        .filter((fieldname, index, arr) => arr.indexOf(fieldname) === index); // Dedupe
      
      // Move required fields to the front (remove from current position, then prepend)
      const requiredInOrder: string[] = [];
      const nonRequiredInOrder: string[] = [];
      
      sanitized.forEach(fieldname => {
        if (requiredFieldnames.has(fieldname)) {
          requiredInOrder.push(fieldname);
        } else {
          nonRequiredInOrder.push(fieldname);
        }
      });
      
      // Add any required fields that weren't in the order
      const requiredNotInOrder = Array.from(requiredFieldnames).filter(
        f => !requiredInOrder.includes(f)
      );
      
      const next = [...requiredNotInOrder, ...requiredInOrder, ...nonRequiredInOrder];

      // Auto-inject synthetic UI columns (e.g. __thumbnail) from defaultVisibleOrder
      // into existing stored settings if they're missing. This ensures that when a
      // synthetic field is promoted to defaultVisible: true, existing users get it
      // without needing to reset their list settings.
      for (const field of defaultVisibleOrder) {
        if (field.startsWith("__") && !next.includes(field)) {
          next.push(field);
        }
      }

      // Pin synthetic UI columns (like thumbnails) to the far-left for consistent UX.
      // Note: We still let users toggle it on/off, but not place it mid-table.
      const THUMBNAIL_FIELD = "__thumbnail";
      if (next.includes(THUMBNAIL_FIELD)) {
        const filtered = next.filter((f) => f !== THUMBNAIL_FIELD);
        filtered.unshift(THUMBNAIL_FIELD);
        return filtered;
      }

      return next;
    }
    
    // Initialize from defaultVisibleOrder, filtered by availableFields
    const filtered = defaultVisibleOrder.filter(f => availableFieldnames.has(f));
    
    // Move required fields to the front (same logic as above)
    const requiredInDefault: string[] = [];
    const nonRequiredInDefault: string[] = [];
    
    filtered.forEach(fieldname => {
      if (requiredFieldnames.has(fieldname)) {
        requiredInDefault.push(fieldname);
      } else {
        nonRequiredInDefault.push(fieldname);
      }
    });
    
    // Add any required fields that weren't in the default
    const requiredNotInDefault = Array.from(requiredFieldnames).filter(
      f => !requiredInDefault.includes(f)
    );
    
    const next = [...requiredNotInDefault, ...requiredInDefault, ...nonRequiredInDefault];

    // Same pinning rule as above for synthetic UI columns.
    const THUMBNAIL_FIELD = "__thumbnail";
    if (next.includes(THUMBNAIL_FIELD)) {
      const filtered = next.filter((f) => f !== THUMBNAIL_FIELD);
      filtered.unshift(THUMBNAIL_FIELD);
      return filtered;
    }

    return next;
  }, [storedSettings, defaultVisibleOrder, availableFields]);

  // Use updateSettings (patch), not setSettings (replace) to preserve future fields
  // CRITICAL: Prevent reordering of pinned synthetic columns (e.g., __thumbnail).
  // These columns must always appear at the leftmost position for consistent UX.
  const setVisibleFieldOrder = (order: string[]) => {
    const THUMBNAIL_FIELD = "__thumbnail";
    const requiredFieldnames = new Set(
      availableFields.filter(f => f.required).map(f => f.fieldname)
    );
    
    // Ensure required fields are always included in order, even if user removed them
    const missingRequired = Array.from(requiredFieldnames).filter(
      f => !order.includes(f)
    );
    const orderWithRequired = [...missingRequired, ...order];
    
    // If __thumbnail is in the order, ensure it's at position 0 (leftmost).
    // Remove it from its current position and prepend it.
    if (orderWithRequired.includes(THUMBNAIL_FIELD)) {
      const filtered = orderWithRequired.filter((f) => f !== THUMBNAIL_FIELD);
      filtered.unshift(THUMBNAIL_FIELD);
      updateSettings(key, { visibleFieldOrder: filtered });
      return;
    }
    
    updateSettings(key, { visibleFieldOrder: orderWithRequired });
  };

  const toggleField = (fieldname: string, enabled: boolean) => {
    const field = availableFields.find(f => f.fieldname === fieldname);
    if (field?.required && !enabled) return; // Cannot hide required fields

    if (enabled) {
      // Add to end (or after last non-required)
      const current = visibleFieldOrder;
      if (!current.includes(fieldname)) {
        // Synthetic UI column: always insert at the beginning (leftmost).
        if (fieldname === "__thumbnail") {
          setVisibleFieldOrder([fieldname, ...current]);
          return;
        }

        const lastNonRequiredIndex = [...current].reverse().findIndex(
          f => !availableFields.find(af => af.fieldname === f)?.required
        );
        const insertIndex = lastNonRequiredIndex === -1 
          ? current.length 
          : current.length - lastNonRequiredIndex;
        const newOrder = [...current];
        newOrder.splice(insertIndex, 0, fieldname);
        setVisibleFieldOrder(newOrder);
      }
    } else {
      // Remove from order
      setVisibleFieldOrder(visibleFieldOrder.filter(f => f !== fieldname));
    }
  };

  const resetToDefaults = () => {
    resetSettings(key);
  };

  return {
    visibleFieldOrder,
    availableFields,
    setVisibleFieldOrder,
    toggleField,
    resetToDefaults,
    isLoading: false, // Static mode doesn't have loading state
  };
}

